import { ChevronRight, ArrowUpDown } from 'lucide-react';
import { useState } from 'react';
import type { Board, Task } from '../../types';
import { useApp } from '../../context/AppContext';
import { getPriorityConfig, getStatusConfig, formatDate } from '../../utils/helpers';
import Avatar from '../common/Avatar';
import Badge from '../common/Badge';
import { LabelBadge } from '../common/Badge';

interface ListViewProps {
  board: Board;
}

type SortField = 'title' | 'priority' | 'status' | 'dueDate' | 'storyPoints';

const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
const statusOrder = { backlog: 0, todo: 1, in_progress: 2, in_review: 3, done: 4 };

export default function ListView({ board }: ListViewProps) {
  const { getTasksForBoard, getUserById, getLabelById, dispatch } = useApp();
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortAsc, setSortAsc] = useState(true);

  const tasks = getTasksForBoard(board.id);

  const sorted = [...tasks].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'title':
        cmp = a.title.localeCompare(b.title);
        break;
      case 'priority':
        cmp = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      case 'status':
        cmp = statusOrder[a.status] - statusOrder[b.status];
        break;
      case 'dueDate':
        cmp = (a.dueDate || '').localeCompare(b.dueDate || '');
        break;
      case 'storyPoints':
        cmp = a.storyPoints - b.storyPoints;
        break;
    }
    return sortAsc ? cmp : -cmp;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 transition-colors"
    >
      {children}
      <ArrowUpDown size={12} className={sortField === field ? 'text-brand-500' : 'text-slate-300'} />
    </button>
  );

  return (
    <div className="p-4 md:p-6">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4">
                  <SortHeader field="title">Tarefa</SortHeader>
                </th>
                <th className="text-left py-3 px-4">
                  <SortHeader field="status">Status</SortHeader>
                </th>
                <th className="text-left py-3 px-4">
                  <SortHeader field="priority">Prioridade</SortHeader>
                </th>
                <th className="text-left py-3 px-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Responsável</span>
                </th>
                <th className="text-left py-3 px-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Etiquetas</span>
                </th>
                <th className="text-center py-3 px-4">
                  <SortHeader field="storyPoints">Pontos</SortHeader>
                </th>
                <th className="text-left py-3 px-4">
                  <SortHeader field="dueDate">Prazo</SortHeader>
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((task) => (
                <TaskRow key={task.id} task={task} getUserById={getUserById} getLabelById={getLabelById} dispatch={dispatch} />
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-sm text-slate-400">
                    Nenhuma tarefa encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TaskRow({
  task,
  getUserById,
  getLabelById,
  dispatch,
}: {
  task: Task;
  getUserById: (id: string) => ReturnType<typeof useApp>['getUserById'] extends (id: string) => infer R ? R : never;
  getLabelById: (id: string) => ReturnType<typeof useApp>['getLabelById'] extends (id: string) => infer R ? R : never;
  dispatch: ReturnType<typeof useApp>['dispatch'];
}) {
  const priority = getPriorityConfig(task.priority);
  const status = getStatusConfig(task.status);
  const assignee = task.assigneeIds[0] ? getUserById(task.assigneeIds[0]) : null;
  const taskLabels = task.labelIds.map((id) => getLabelById(id)).filter(Boolean);

  return (
    <tr
      onClick={() => dispatch({ type: 'SELECT_TASK', payload: task.id })}
      className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors group"
    >
      <td className="py-3 px-4">
        <span className="text-sm font-medium text-slate-700 group-hover:text-brand-600 transition-colors">
          {task.title}
        </span>
      </td>
      <td className="py-3 px-4">
        <Badge className={status.color}>{status.label}</Badge>
      </td>
      <td className="py-3 px-4">
        <Badge className={priority.color} dot={priority.dot}>
          {priority.label}
        </Badge>
      </td>
      <td className="py-3 px-4">
        {assignee && (
          <div className="flex items-center gap-2">
            <Avatar name={assignee.name} color={assignee.avatarColor} size="sm" />
            <span className="text-sm text-slate-600 hidden md:inline">{assignee.name}</span>
          </div>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1">
          {taskLabels.slice(0, 2).map((label) => (
            <LabelBadge key={label!.id} name={label!.name} color={label!.color} />
          ))}
          {taskLabels.length > 2 && (
            <span className="text-xs text-slate-400">+{taskLabels.length - 2}</span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        {task.storyPoints > 0 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-100 text-xs font-semibold text-slate-600">
            {task.storyPoints}
          </span>
        )}
      </td>
      <td className="py-3 px-4">
        {task.dueDate && (
          <span className="text-sm text-slate-500">{formatDate(task.dueDate)}</span>
        )}
      </td>
      <td className="py-3 px-4">
        <ChevronRight size={16} className="text-slate-300 group-hover:text-brand-400 transition-colors" />
      </td>
    </tr>
  );
}
